import { ReactNode, useEffect, useState, useRef, CSSProperties } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

interface SlideTransitionProps {
  children: ReactNode;
  slideDistance?: number; // Distance to slide in pixels
  duration?: number; // Animation duration in ms
}

/**
 * SlideDirectionTransition
 * 
 * A transition component that slides content left/right based on navigation direction:
 * - When moving forward: slides old content left and new content in from right
 * - When moving backward: slides old content right and new content in from left
 * 
 * Automatically detects direction based on browser history and React Router navigation.
 */
export function SlideDirectionTransition({ 
  children, 
  slideDistance = 50, 
  duration = 300 
}: SlideTransitionProps) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [displayLocation, setDisplayLocation] = useState(location);
  
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Store previous paths to determine navigation direction
  const pathHistoryRef = useRef<string[]>([location.pathname]);
  
  // Store previous children for exit animation
  const [prevChildren, setPrevChildren] = useState<ReactNode>(null);
  
  // Determine if we're navigating forward or backward
  const determineNavigationDirection = (newLocation: typeof location): 'forward' | 'backward' => {
    if (navigationType === 'POP') {
      // Browser back button was pressed
      return 'backward';
    }
    
    const history = pathHistoryRef.current;
    const pathIndex = history.indexOf(newLocation.pathname);
    
    // If we're going back to a path we've visited before
    if (pathIndex !== -1 && pathIndex < history.length - 1) {
      // Truncate history at this point
      pathHistoryRef.current = history.slice(0, pathIndex + 1);
      return 'backward';
    } 
    
    // We're navigating to a new path
    pathHistoryRef.current = [...history, newLocation.pathname];
    return 'forward';
  };

  // Start transition animation between pages
  const startTransition = (newDirection: 'forward' | 'backward') => {
    setPrevChildren(children);
    setDirection(newDirection);
    setIsTransitioning(true);
    
    // After animation completes, update display location and clean up
    setTimeout(() => {
      setDisplayLocation(location);
      setIsTransitioning(false);
      setPrevChildren(null);
    }, duration);
  };
  
  useEffect(() => {
    // Only trigger transition when the path changes
    if (location.pathname !== displayLocation.pathname) {
      const newDirection = determineNavigationDirection(location);
      startTransition(newDirection);
    }
  }, [location, displayLocation.pathname, navigationType, duration, children]);

  // When not transitioning, render children normally
  if (!isTransitioning) {
    return (
      <div style={{ width: '100%', height: '100%' }}>
        {children}
      </div>
    );
  }
  
  return <AnimatedTransition 
    prevChildren={prevChildren}
    newChildren={children}
    direction={direction}
    slideDistance={slideDistance}
    duration={duration}
  />;
} 

interface AnimatedTransitionProps {
  prevChildren: ReactNode;
  newChildren: ReactNode;
  direction: 'forward' | 'backward';
  slideDistance: number;
  duration: number;
}

// Component that handles the animation between pages
function AnimatedTransition({
  prevChildren,
  newChildren,
  direction,
  slideDistance,
  duration
}: AnimatedTransitionProps) {
  const halfDuration = Math.floor(duration / 2);
  
  // Container for both animations
  const containerStyle: CSSProperties = {
    position: 'relative', 
    width: '100%', 
    height: '100%', 
    overflow: 'hidden'
  };

  // Direction-based transforms
  const exitTransform = direction === 'forward' 
    ? `translateX(-${slideDistance}px)` 
    : `translateX(${slideDistance}px)`;
    
  const enterInitialTransform = direction === 'forward' 
    ? `translateX(${slideDistance}px)` 
    : `translateX(-${slideDistance}px)`;
  
  // Styles for the outgoing/exiting content
  const exitStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    transition: `transform ${duration}ms ease, opacity ${halfDuration}ms ease`,
    transform: exitTransform,
    opacity: 0,
    zIndex: 1
  };
  
  // Base styles for the incoming content
  const enterStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    transition: `transform ${duration}ms ease, opacity ${halfDuration}ms ease ${halfDuration}ms`, // Delay opacity transition
    transform: 'translateX(0)', // Final position
    opacity: 1,
    zIndex: 2
  };
  
  // Initial position for the entering content
  const initialEnterStyle: CSSProperties = {
    transform: enterInitialTransform,
    opacity: 0
  };

  // Apply animation to enter the new content
  const startEnterAnimation = (element: HTMLDivElement | null) => {
    if (element) {
      requestAnimationFrame(() => {
        element.style.transform = 'translateX(0)';
        element.style.opacity = '1';
      });
    }
  };
  
  return (
    <div style={containerStyle}>
      {/* Container for exit animation */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none',
      }}>
        {/* Exiting content */}
        <div style={exitStyle}>
          {prevChildren}
        </div>
      </div>
      
      {/* Container for enter animation */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 2,
      }}>
        {/* Entering content - starts offscreen and slides in */}
        <div 
          style={{ 
            ...enterStyle,
            ...initialEnterStyle
          }}
          ref={startEnterAnimation}
        >
          {newChildren}
        </div>
      </div>
    </div>
  );
} 